import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/config";
import { SEO } from "@/components/SEO";
import { METIERS, VILLES, METIERS_LIST, VILLES_LIST } from "@/data/seoMetiers";
import { Briefcase, MapPin, CheckCircle, ArrowRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Page SEO longue traîne : /metiers/:metier ou /metiers/:metier/:ville
 * Contenu dense + H1 unique + JSON-LD JobPosting list pour Google Jobs rich snippets.
 */
export default function MetierPage() {
  const { metier: metierSlug, ville: villeSlug } = useParams();
  const metier = METIERS[metierSlug];
  const ville = villeSlug ? VILLES[villeSlug] : null;

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/offers?status=active`);
        const all = r.data || [];
        // Filtre texte côté client : match métier + ville (optionnel)
        const metierRegex = new RegExp(metier?.label?.split(" ")[0] || "", "i");
        let filtered = all.filter((o) => {
          const haystack = `${o.title || ""} ${o.description || ""} ${o.profile_needed || ""}`;
          return metierRegex.test(haystack);
        });
        if (ville) {
          const villeRegex = new RegExp(ville.label, "i");
          filtered = filtered.filter((o) => villeRegex.test(o.location || ""));
        }
        setOffers(filtered.slice(0, 20));
      } catch {
        setOffers([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [metierSlug, villeSlug, metier, ville]);

  if (!metier) return <Navigate to="/offres" replace />;
  if (villeSlug && !ville) return <Navigate to={`/metiers/${metierSlug}`} replace />;

  // SEO title dynamique
  const baseTitle = `${metier.label} — Offres d'intérim logistique`;
  const title = ville ? `${metier.label} à ${ville.label} — Offres d'intérim logistique` : baseTitle;
  const description = ville
    ? `${metier.intro.slice(0, 120)}… Découvrez nos offres de ${metier.label.toLowerCase()} à ${ville.label} (${ville.region}) : intérim, CDD, CDI.`
    : `${metier.intro.slice(0, 140)}… Inscription gratuite.`;
  const path = ville ? `/metiers/${metierSlug}/${villeSlug}` : `/metiers/${metierSlug}`;

  // JSON-LD ItemList de JobPosting pour Google Jobs
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": title,
    "itemListElement": offers.map((o, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "item": {
        "@type": "JobPosting",
        "title": o.title,
        "description": (o.description || "").slice(0, 500),
        "datePosted": o.created_at,
        "employmentType": "TEMPORARY",
        "hiringOrganization": {
          "@type": "Organization",
          "name": "FR INTERIM",
          "sameAs": "https://fr-interim.fr",
        },
        "jobLocation": {
          "@type": "Place",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": o.location || ville?.label || "France",
            "addressCountry": "FR",
          },
        },
        "industry": "Logistique et supply chain",
      },
    })),
  };

  return (
    <main className="min-h-screen bg-white" data-testid="metier-page">
      <SEO title={title} description={description} path={path} jsonLd={jsonLd} keywords={metier.keywords} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#662483] to-[#0069B4] text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <nav className="text-xs text-white/70 mb-4">
            <Link to="/" className="hover:underline">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/offres" className="hover:underline">Offres d'emploi</Link>
            <span className="mx-2">/</span>
            <Link to={`/metiers/${metierSlug}`} className="hover:underline">{metier.label}</Link>
            {ville && (<><span className="mx-2">/</span><span>{ville.label}</span></>)}
          </nav>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-4" data-testid="metier-h1">
            {ville ? `${metier.label} à ${ville.label}` : metier.h1}
          </h1>
          <p className="text-base text-white/90 max-w-3xl leading-relaxed">
            {ville
              ? `Retrouvez toutes nos offres d'intérim de ${metier.label.toLowerCase()} à ${ville.label} (${ville.region}) et ses environs. FR INTERIM vous accompagne pour trouver votre prochaine mission dans les meilleures entreprises logistiques.`
              : metier.intro}
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <Button asChild className="bg-[#E5CD96] text-[#3F3F3F] hover:bg-[#d4ba7e] rounded-none font-semibold">
              <Link to="/auth" data-testid="metier-cta-register">
                S'inscrire gratuitement <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-none bg-transparent border-white text-white hover:bg-white/10 font-semibold">
              <Link to="/offres">Voir toutes les offres</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Missions + prérequis */}
      {!ville && (
        <section className="py-14 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10">
            <div>
              <h2 className="text-xl font-bold text-[#3F3F3F] mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-[#662483]" /> Vos missions
              </h2>
              <ul className="space-y-3">
                {metier.missions.map((m, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-[#0069B4] shrink-0 mt-0.5" />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#3F3F3F] mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#662483]" /> Prérequis
              </h2>
              <ul className="space-y-3">
                {metier.prerequis.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-[#E5CD96] shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Offres filtrées */}
      <section className="bg-[#FAFAFA] py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold text-[#3F3F3F] mb-2">
            Offres {metier.label.toLowerCase()}{ville ? ` à ${ville.label}` : ""} disponibles
          </h2>
          <p className="text-sm text-gray-500 mb-6">{offers.length} offre{offers.length > 1 ? "s" : ""} correspondante{offers.length > 1 ? "s" : ""}</p>
          {loading ? (
            <p className="text-sm text-gray-500">Chargement…</p>
          ) : offers.length === 0 ? (
            <div className="bg-white border border-gray-200 p-8 text-center">
              <p className="text-sm text-gray-600 mb-4">
                Aucune offre {metier.label.toLowerCase()}{ville ? ` à ${ville.label}` : ""} pour le moment.
              </p>
              <p className="text-xs text-gray-500 mb-4">
                Inscrivez-vous pour être alerté dès qu'une nouvelle offre correspond à votre profil.
              </p>
              <Button asChild className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold">
                <Link to="/auth">Créer mon profil</Link>
              </Button>
            </div>
          ) : (
            <div className="grid gap-3">
              {offers.map((o) => (
                <Link
                  to="/offres"
                  key={o.offer_id}
                  className="bg-white border border-gray-200 hover:border-[#662483] p-4 transition-colors"
                  data-testid={`metier-offer-${o.offer_id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[11px] font-bold text-[#662483]">{o.offer_number || "—"}</span>
                    {o.location && <span className="text-[11px] text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{o.location}</span>}
                    {o.hours && <span className="text-[11px] text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" />{o.hours}</span>}
                  </div>
                  <h3 className="text-sm font-semibold text-[#3F3F3F]">{o.title}</h3>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Maillage interne : autres villes ou autres métiers */}
      <section className="py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold text-[#3F3F3F] mb-4">
            {ville ? `Autres villes pour le poste de ${metier.label.toLowerCase()}` : `${metier.label} par ville`}
          </h2>
          <div className="flex flex-wrap gap-2 mb-10">
            {VILLES_LIST.map((v) => (
              <Link
                key={v.slug}
                to={`/metiers/${metierSlug}/${v.slug}`}
                className={`px-3 py-1.5 border text-xs font-medium transition-colors ${v.slug === villeSlug ? "bg-[#662483] text-white border-[#662483]" : "bg-white text-gray-700 border-gray-200 hover:border-[#662483] hover:text-[#662483]"}`}
                data-testid={`ville-link-${v.slug}`}
              >
                {metier.label} à {v.label}
              </Link>
            ))}
          </div>

          <h2 className="text-xl font-bold text-[#3F3F3F] mb-4">Autres métiers</h2>
          <div className="flex flex-wrap gap-2">
            {METIERS_LIST.filter((m) => m.slug !== metierSlug).map((m) => (
              <Link
                key={m.slug}
                to={`/metiers/${m.slug}`}
                className="px-3 py-1.5 border bg-white text-gray-700 border-gray-200 hover:border-[#0069B4] hover:text-[#0069B4] text-xs font-medium transition-colors"
                data-testid={`metier-link-${m.slug}`}
              >
                {m.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
